/* Copyright 2011-2020 The MathWorks, Inc. */

define([
    "dojo/aspect",
    "dojo/on",
    "dojo/dom",
    "dojo/topic",
    "dojo/keys",
    "dojo/_base/declare",
    "dojo/dom-construct",
    "dojo/_base/lang",
    "dojo/_base/kernel",
    "dijit/Viewport",
    "dojo/query",
    "dojo/dom-geometry",
    "../ToolstripUtils",
    "dojo/i18n!l10n/nls/toolstrip",
    "dojo/_base/event",
    "dijit/registry",
    "dijit/popup",
    "dojo/text!./templates/Toolstrip.html",
    "dijit/_WidgetBase",
    "dijit/_Container",
    "dijit/_TemplatedMixin",
    "./TabContainer",
    "mw-form/PushButton",
    "mw-form/ContextMenu",
    "mw-form/MenuItem",
    "mw-form/MenuSeparator",
    "mw-form/MenuHeader",
    "../hashes/ComponentsEligibleForQAB",
    "../constants/TypeConstants",
    "../constants/DisplayStateConstants",
    "../constants/DisplayModeConstants",
    "../constants/MiscConstants",
    "./_WidgetMixin",
    "../utils/doubleClickHandler",
    "mw-utils/widgetUtils",
    "mw-notifications/Notifications",
    "mw-form/mixins/PreventSelectionMixin",
    "../mixins/TouchMenuMixin",
    "MW/uiframework/MnemonicsStates",
    "MW/uiframework/utils",
    "MW/toolstrip/contextmenumanager/ContextMenuManager",
    "MW/toolstrip/constants/QABAuthoringModeConstants"
], function (aspect, on, dom, topic, keys, declare, domConstruct, lang, kernel, Viewport, query, domGeometry, ToolstripUtils,
             toolstripL10n, dojoEvent, registry, popup, ToolstripHtml, _WidgetBase, _Container, _TemplatedMixin, TabContainer,
             Button, ContextMenu, MenuItem, MenuSeparator, MenuHeader, ComponentsEligibleForQAB, TypeConstants, DisplayStateConstants,
             DisplayModeConstants, MiscConstants, _WidgetMixin, doubleClickHandler, widgetUtils, Notifications,
             PreventSelectionMixin, TouchMenuMixin, MnemonicsStates, utils, ContextMenuManager, QABAuthoringModeConstants) {

    var matchesSelector = function (/*Element*/ element, /*String*/ selector) { // TODO: use domUtils.matchesSelector instead
        var matchesMethod;

        if (!element || !selector) {
            return false;
        }

        if (element.msMatchesSelector) {
            matchesMethod = "msMatchesSelector";
        } else {
            matchesMethod = "matches";
        }

        return element[matchesMethod](selector);
    };

    return declare([_WidgetBase, _Container, _TemplatedMixin, _WidgetMixin, PreventSelectionMixin, TouchMenuMixin], {
        /** @lends Toolstrip# */

        templateString: ToolstripHtml,

        /**
         * Toolstrip is a Container which can have Tabs as it's children.
         * @extends dijit/_WidgetBase
         * @constructs
         */
        constructor: function () {
            this._shouldSetSelectedTab = true;
            this._previousSelectedChild = null;
            this._tabGroupListeners = {};
            // document.onClick handler:
            // saving a reference to the bounded function, so we can easily remove the listener on destruction
            this._handleClick = function (event) {
                // Gallery drop down menu opens on top of the arrow node.
                // So, the event.target is no longer be the arrow node, but is the body.
                if (this.displayState === DisplayStateConstants.EXPANDED_ON_TOP && !widgetUtils.isDescendant(event.target, this.domNode) && event.target !== document.body) {
                    this.set("displayState", DisplayStateConstants.COLLAPSED);
                }
            }.bind(this);
            aspect.before(this, "addChild", this._beforeAddChild);
            // Register the context menu
            this._setupContextMenu();
        },

        /**
         * @override
         */
        postCreate: function () {
            this.inherited(arguments);
            this.domNode.setAttribute("data-focus", "refuse");

            var that = this;

            this.tabContainer = new TabContainer({
                'class': "tabContainer"
            }, this.toolstripTabContainer);

            // Block tablist from handling keydown since all keyboard navigation is handled by MnemonicsManager
            this.tabContainer.tablist.onkeydown = function () {};

            this._supportingWidgets.push(this.tabContainer);

            this.own(aspect.after(this, 'placeAt', function () {
                // This hook is used by the UE team to detect when the Toolstrip was placed in the
                // DOM. So that they can manage their window.
                topic.publish('/Toolstrip/toolstripPlaced', {toolstripId: this.id});
                that.toolstripPlaced();
                //Call resize after toolstrip is placed
                that.resize();
            }));

            this.collapseButton = new Button({
                'class': "collapseButton",
                icon: "smallCollapse",
                showText: false
            }).placeAt(this.collapseButtonContainer);

            // To ensure that the title attribute value shows up as a label in screen reader's elements dialog.
            this.collapseButton.focusNode.removeAttribute("aria-labelledby");

            // Set "data-mnemonic-key-nav-direction" to allow the collapse button to be included in the toolstrip tab
            // controls keyboard navigation cycle.
            this.collapseButton.focusNode.setAttribute("data-mnemonic-key-nav-direction", "all");

            this.collapseButton.on('click', lang.hitch(this, this._expandCollapse));

            // Prevent the synthetic click event from dojo/touch which cause the context menu to close immediately
            this.tabContainer.tablist.tablistWrapper.children[0].dojoClick = false;

            // Show Tooltip for Expand collapse button
            this._setTooltipforExpandCollapseButton();
            this._supportingWidgets.push(this.collapseButton);

            if (this.hostId) {
                this.placeAt(this.hostId);
                this.resize();
                this.startup();
            }

            this.connect(this.tabContainer, 'selectChild', function (tab) {
                this.set('selectedTab', tab.id);
            });

            this.own(Viewport.on("resize", function () {
                this.resize();
            }.bind(this)));

            var handleSingleClickOnTab = function (event) {
                var tabWidget = registry.getEnclosingWidget(event.target);

                // If a tab is clicked and the toolstrip is currently collapsed, then expand it on top.
                if (that.displayState === DisplayStateConstants.COLLAPSED) {
                    that.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                    // If a tab is clicked and the toolstrip is currently expanded on top, then collapse it only if the
                    // currently selected tab is clicked again. The mnemonics state check is to ensure that the toolstrip
                    // is not collapsed when Mnemonics are shown on it in expanded on top state.

                } else if (that.displayState === DisplayStateConstants.EXPANDED_ON_TOP
                    && MnemonicsStates.getCurrentMnemonicsState() !== MnemonicsStates.CONTROL_TAB) {
                    // If the clicked node is a descendant of the currently selected tab then collapse the toolstrip
                    if (that._previousSelectedChild && dom.isDescendant(event.target, that._previousSelectedChild.domNode)) {
                        that.set("displayState", DisplayStateConstants.COLLAPSED);
                    }
                }

                // Remember the last clicked tab
                that._previousSelectedChild = tabWidget;
            };

            var handleDoubleClickOnTab = function () {
                that._expandCollapse();
            };
            
            var matchesSelector = function (node, selector) {
                var matchesMethod;

                if (!node || !selector || !(node instanceof Element)) {
                    return false;
                }

                if (node.msMatchesSelector) {
                    matchesMethod = "msMatchesSelector";
                } else {
                    matchesMethod = "matches";
                }

                return node[matchesMethod](selector);
            };

            this.own(
                on(this.domNode, ".dijitTab:click",
                    doubleClickHandler.createCallback(handleSingleClickOnTab, handleDoubleClickOnTab)),
                
                on(window, "blur", function (e) {
                    setTimeout(function () {
                        if (!matchesSelector(document.activeElement, '.toolstrip, .toolstrip *') &&
                            (document.activeElement.tagName !== 'IFRAME') &&
                            (that.displayState === DisplayStateConstants.EXPANDED_ON_TOP)) {
                            that.set("displayState", DisplayStateConstants.COLLAPSED);                        
                            flagForCollapse = true;
                        }
                    }.bind(this), 0);
                }.bind(this))
            );
            // finish setup of the context menu that was missed before
            this._setupContextMenu();

            this._setLocaleOnDomNode();

            this.domNode.setAttribute("role", "region");
            this.domNode.setAttribute("aria-label", toolstripL10n.toolstripLabel);
            this.tabContainer.tablist.containerNode.setAttribute("aria-label", toolstripL10n.toolstripLabel);

            this.domNode.addEventListener("keydown", this._handleKeyDown, true);
            // Fix for g2019545: attaching _handleClick callback in capture phase.
            // Because in case the element is destroyed onClick, there is no way to
            // check if it was a descendent of toolstrip
            document.addEventListener("click", this._handleClick, true)
            
            // g2013336: In order to have OS specific CSS, we need to detect the OS in JS and add an appropriate class
            this._detectOS();
        },

        /**
         * Adds a button at the top left part of the toolstrip.
         */
        addTopLeftButton: function () {
            this._topLeftButton = document.createElement('div');
            this._topLeftButton.setAttribute('role', 'button');
            this._topLeftButton.setAttribute('title', toolstripL10n.homeButtonLabel);
            this._topLeftButton.classList.add('topLeftButton');

            var topLeftIcon = document.createElement('div');
            topLeftIcon.classList.add('topLeftIcon');
            this._topLeftButton.appendChild(topLeftIcon);
            this.tabContainer.tablist.tablistWrapper.insertBefore(this._topLeftButton, this.tabContainer.tablist.containerNode);

            this._topLeftButtonListeners = []; // Have this as an array to be able to add keyboard listeners if required in the future
            this._topLeftButtonListeners.push(utils.addEventListener(this._topLeftButton, 'click', this._handleTopLeftButtonClick.bind(this)));
        },

        _handleTopLeftButtonClick: function () {
            var customEvent = new Event('topLeftButtonClicked');
            window.dispatchEvent(customEvent);
        },

        _handleKeyDown: function (e) {
            var isMac = utils.isMac();

            // Check if Ctrl + A / Command + A is pressed within or on the toolstrip node. Do not do preventDefault
            // for Ctrl + A / Command + A for input and textarea elements.
            if (["INPUT", "TEXTAREA"].indexOf(e.target.nodeName) === -1
                && e.keyCode === 65 && ((isMac && e.metaKey) || (!isMac && e.ctrlKey))) {
                // g2015606: Do preventDefault to disable text selection
                e.preventDefault();
            }
        },

        /**
         * This is used in the CSS to have bigger fonts in the Tab title and the Section titles for Korean, Japanese
         * and Chinese locales
         * @private
         */
        _setLocaleOnDomNode: function () {
            if (["ko", "ko-kr", "ja-jp", "zh-cn"].indexOf(kernel.locale) > -1) {
                switch (kernel.locale) {
                    case "ko":
                    case "ko-kr":
                        document.body.classList.add("isKoreanToolstrip");
                        break;

                    case "ja-jp":
                        document.body.classList.add("isJapaneseToolstrip");
                        break;

                    case "zh-cn":
                        document.body.classList.add("isChineseToolstrip");
                        break;
                }
                document.body.classList.add("isCJKToolstrip");
            }
        },
        
        _detectOS: function () {
            switch (utils.getOS()) {
                case utils.OSConstants.WINDOWS:
                    document.body.classList.add("os-win");
                    break;
                case utils.OSConstants.MAC:
                    document.body.classList.add("os-mac");
                    document.body.classList.add("os-unix"); // We may want to target all unix
                    break;
                case utils.OSConstants.LINUX:
                    document.body.classList.add("os-linux");
                    document.body.classList.add("os-unix"); // We may want to target all unix
                    break;
            }
        },

        _setupContextMenu: function () {
            // Can't use simple #id since dojo produces ids beginning with numbers which are not valid for querySelector
            var that = this,
                idSelector = '[id="' + this.id + '"]',
                popupSelectorFcn = function (e) {
                    var activeElement = e.target || document.activeElement,
                        allDijitPopups = query('[dijitpopupparent]'),
                        activePopup;

                    allDijitPopups.some(function (popup) {
                        if ((popup.style.display !== "none") && (activeElement && popup.contains(activeElement))) {
                                activePopup = popup;
                                return true;
                        }
                        return false;
                    });

                    // If the toolstrip contains the invoking widget of the popup
                    if (activeElement && activePopup && (activePopup.classList.contains("galleryPopupWrapper")
                        || activePopup.classList.contains("toolstripPopup")
                        || activePopup.querySelector('[data-type="PopupList"]')) // popups opened through toolstrip controls
                        && this._findParentNode(activeElement, "id", this.id)) {

                        var popupChildren = query('[id="' + activePopup.id + '"] *');
                        popupChildren.push(activePopup);
                        if (popupChildren.indexOf(activeElement) > -1) {
                            return popupChildren;
                        }
                        
                    } else {
                        return null;
                    }
                }.bind(this);

            if (!this._contextMenu) {
                var contextMenuCloseListener = function() {
                    this._contextMenu.domNode.removeEventListener("close", contextMenuCloseListener);
                }.bind(this);
            
                this._contextMenu = new ContextMenu({
                    willOpenCallback: lang.hitch(this, this._updateContextMenu),
                    //The context menu should show on the Toolstrip node and all of its descendants
                    targetNodes: [idSelector, idSelector + " *", popupSelectorFcn]
                });
                
                this._contextMenu.domNode.addEventListener("close", contextMenuCloseListener);
            
                // Add class to allow for focus manager to know when to close context menu and gallery popup
                this._contextMenu.domNode.classList.add("mwToolstripPopupContextMenu");

                ContextMenuManager.registerContextMenu(this._contextMenu);

                this.own({
                    remove: function () {
                        ContextMenuManager.unregisterContextMenu(that._contextMenu);
                        that._contextMenu.destroyRecursive();
                    }
                });
            } else {
                this._contextMenu.set("targetNodes", [idSelector, idSelector + " *", popupSelectorFcn]);
                if (this.get("displayMode") === DisplayModeConstants.COMPACT) {
                    this._contextMenu.domNode.classList.add("mwCompactToolstripPopupContextMenu");
                }
            }
        },

        // TODO: Update mw-dom-utils/domUtils to fix bug once we figure out how to work in 2P
        _findParentNode: function (node, /*String*/ selector, /*String?*/ modifier) {
            var parent = null, parentPopupId;

            if (node.domNode) {
                node = node.domNode;
            }

            while (node && node !== window.document) {
                if (modifier) {
                    if (node.hasAttribute(selector) && (node.getAttribute(selector) === modifier)) {
                        parent = node;
                        break;
                    }
                } else {
                    if (node.hasAttribute(selector)) {
                        parent = node;
                        break;
                    }

                    if (node.classList.contains(selector)) {
                        parent = node;
                        break;
                    }
                }

                parentPopupId = node.getAttribute("dijitpopupparent");

                // If the widget is in one of the popups, then go up the popup parent chain
                if (parentPopupId) {
                    node = document.getElementById(parentPopupId);
                    // If its not one of the popups, then go to parent
                } else {
                    node = node.parentNode;
                }
            }

            return parent;
        },

        // TODO: add dependency capability, to allow for callback to be fired after another one
        addContextMenuCallback: function (selector, callback) {
            ContextMenuManager.addContributor(this._contextMenu, selector, callback);
        },

        removeContextMenuCallback: function (selector, callback) {
            ContextMenuManager.removeContributor(this._contextMenu, selector, callback);
        },

        _updateContextMenu: function (e) {
            ContextMenuManager.build(this._contextMenu, e.target, true);

            var widget, disabledState, widgetToBeUpdatedForIsInQABProp;

            if (e.target.classList.contains("toolstripComponentWrapper")) {
                widget = registry.getEnclosingWidget(e.target.children[0]);
            } else {
                widget = registry.getEnclosingWidget(e.target);
            }

            if (widget) { // Some parts of the toolstrip may not be widgets, though that has not caused problems yet

                // If a widget allows editing option and if it is not in editable state already,
                // populate and display a context menu entry for editing the widget
                if (widget.get("editOption") && widget.hasOwnProperty("editable") && !widget.get("editable")) {
                    this._addEditWidgetMenuItemToContextMenu(widget);
                }

                // Add Remove Widget Menu Item for Gallery Item/Toggle Gallery Item if needed
                // Adds the widget specific removable context menu item.
                if (!widget.page && widget.type !== "Tab" && widget.get("removable") === true) {
                    this._addRemoveWidgetMenuItemToContextMenu(widget);
                }

                // Add Remove Widget Menu Item for Tab if needed
                if ((widget.page && widget.page.type === "Tab") && (widget.page.get("removable") === true)) {
                    this._addRemoveWidgetMenuItemToContextMenu(widget.page);
                } else {
                    // Determine if parent Tab is removable
                    var tab = widget;

                    // Stop loop at Tab
                    while (tab && (tab.type !== "Tab")) {
                        if (tab.domNode && tab.domNode.parentElement && tab.domNode.parentElement.hasAttribute("dijitpopupparent")) {
                            tab = registry.byId(tab.domNode.parentElement.getAttribute("dijitpopupparent"));
                        } else {
                            tab = tab.getParent();
                        }
                    }

                    if (tab && (tab.get("removable") === true)) {
                        this._addRemoveWidgetMenuItemToContextMenu(tab);
                    }
                }

                if (this._isListItemTypeWidget(widget)) {
                    this._contextMenu.invokedFromListItemTypeWidget = true;
                }

                // Right clicking the ListItemWithPopup should close its sub menu if its opened
                // TODO: There is another usecase that would fail but its rare enough to not consider it as of
                // now: Right click on any listitem -> It takes 200 msec for the context menu to open up, before this,
                // move the mouse to other ListItemWithPopup that opens a submenu, if you are successful to move the
                // mouse before 200 msec, the context menu (that has "addToQAB" option) gets closed.
                if (widget.get("type") === TypeConstants.LIST_ITEM_WITH_POPUP && widget.isMenuOpen()) {
                    // closeEventData for the submenu is set when that submenu gets focus.
                    // In case we are closing the submenu without ever giving it the focus, closeEventData
                    // might not be populated. Therefore setting the data before closing the submenu.
                    // TODO: figure out a better way to set closeEventData in edge-cases like this
                    widget.get("menu")._closeEventData = {mwEventData: {gainedFocusWidget: widget}};
                    widget.closeMenu();
                }
            }

            if (this.enableQABCustomization) {
                // Only show "addToQAB" option for components that are eligible for QAB
                if (((Object.keys(ComponentsEligibleForQAB).indexOf(widget.get("type")) > -1)
                    && (!widget.isEligibleForQab || widget.isEligibleForQab()))
                    || widget.eligibleForParentAdditionToQAB) {

                    // Do not show "addToQAB" option for components that are within a GalleryPopup Footer. There is
                    // a use case where ListItemWithCheckbox, ListItemWithRadioButton, ListItemWithTextField and
                    // removable ListItem can appear within a footer inside of a GalleryPopup. "addToQAB" option
                    // adds their invokingWidget to QAB and Gallery is not yet supported to be added in QAB. As a
                    // result, it would be weird to show "addToQAB" option only for ListItem and ListItemWithPopup
                    // widgets and not to other widgets within a GalleryPopup. Hence, do not show "addToQAB" option for
                    // widgets within a GalleryPopup Footer. One might argue that a DropDown type of widget also
                    // opens a GalleryPopup and for these cases, the DropDown widget can be added to QAB. But, its
                    // better to hide this option for all GalleryPopups for now, as not doing so, would make the end
                    // users believe that there is some inconsistency within GalleryPopups launched from a Gallery vs a
                    // DropDown type of widget.
                    if (!this._isWidgetWithinGalleryPopupFooter(widget)) {
                        // Add a separator between last child and "Minimize Toolstrip" Menu Items
                        if (this._contextMenu.hasChildren()) {
                            this._contextMenu.addChild(new MenuSeparator());
                        }

                        if (widget.eligibleForParentAdditionToQAB) {
                            widgetToBeUpdatedForIsInQABProp = this._findCorrespondingWidgetForIsInQABProp(widget);
                        } else {
                            widgetToBeUpdatedForIsInQABProp = widget;
                        }

                        if (widgetToBeUpdatedForIsInQABProp) {
                            disabledState = widgetToBeUpdatedForIsInQABProp.get("isInQAB");


                            var queryWidgetInQABWithActionId = function (actionId) {
                                // find widget in QAB that has the given actionId
                                var widgetId;
                                this._QABWidget.getChildren().some(function (qaGroup) {
                                    return qaGroup.getChildren().some(function (candidateWidget) {
                                        if (candidateWidget.actionId === actionId) {
                                            widgetId = candidateWidget.id;
                                            return true;
                                        }
                                    });
                                });
                                return widgetId;
                            }.bind(this);

                            var addToQABMenuItem = new MenuItem({
                                text: toolstripL10n.addToQAB,
                                tag: "addToQAB",
                                disabled: disabledState
                            });
                            this._contextMenu.addChild(addToQABMenuItem);
                            addToQABMenuItem.on("click", function () {
                                if (widgetToBeUpdatedForIsInQABProp) {
                                    widgetToBeUpdatedForIsInQABProp._treatAsUserEvent = true; // TODO: Either have a public API on the widget or come up with any other better approach to do this
                                    widgetToBeUpdatedForIsInQABProp.set("isInQAB", true);

                                    var qabControlId;
                                    if (widgetToBeUpdatedForIsInQABProp.actionId) {
                                        qabControlId = queryWidgetInQABWithActionId(widgetToBeUpdatedForIsInQABProp.actionId);
                                    }
                                    // Fire an event "controlAdded"
                                    this._fireQABEvent(widgetToBeUpdatedForIsInQABProp.id, qabControlId, "add");

                                    widgetToBeUpdatedForIsInQABProp._treatAsUserEvent = false;
                                }

                                popup.close(); // close all the popups
                            }.bind(this));
                        }
                    }
                }

                // If the right click happened on a QAB widget then add a "Show/Hide Label" Menu Item
                switch (widget.get("type")) {
                    case TypeConstants.QAB_PUSH_BUTTON:
                    case TypeConstants.QAB_TOGGLE_BUTTON:
                    case TypeConstants.QAB_SPLIT_BUTTON:
                    case TypeConstants.QAB_DROP_DOWN_BUTTON:
                    case TypeConstants.QAB_TOGGLE_SPLIT_BUTTON:
                        if (widget.get("isInQAB") === true) {
                            if (this._contextMenu.hasChildren()) {
                                this._contextMenu.addChild(new MenuSeparator());
                            }

                            var removeFromQABMenuItem = new MenuItem({
                                text: toolstripL10n.removeFromQAB,
                                tag: "removeFromQAB"
                            });
                            this._contextMenu.addChild(removeFromQABMenuItem);
                            removeFromQABMenuItem.on("click", function () {
                                widget._treatAsUserEvent = true; // TODO: Either have a public API on the widget or come up with any other better approach to do this
                                widget.set("isInQAB", false);

                                // Fire an event "controlRemoved"
                                this._fireQABEvent(widget.toolstripControlId, widget.id, "remove");

                                widget._treatAsUserEvent = false;
                            }.bind(this));
                        }

                        this._setShowHideLabelMenuItem(widget);
                        break;

                    // g1607564
                    // Enable show/hide label options for the QAB control of type floating pane dock
                    case TypeConstants.QAB_FLOATING_PANE_DOCK:
                        this._setShowHideLabelMenuItem(widget);
                        break;
                }

                if (!this.get("hideRestoreDefaultsQABOption") && this._isDescendantOfQAB(widget)) {
                    var restoreFactoryControlsMenuItem = new MenuItem({
                        text: toolstripL10n.restoreFactoryControls,
                        tag: "restoreFactoryControls"
                    });
                    this._contextMenu.addChild(restoreFactoryControlsMenuItem);
                    restoreFactoryControlsMenuItem.on("click", lang.hitch(this, function () {
                        if (this.qabAuthoringMode === QABAuthoringModeConstants.MANUAL) {
                            this._QABWidget.restoreFactoryControls();
                        } else {
                            Notifications.displayConfirmDialog(
                                toolstripL10n.restoreFactoryControlsDialogTitle,
                                toolstripL10n.restoreFactoryControlsDialogMessage,
                                {
                                    icon: "warning",
                                    closeCallback: lang.hitch(this, function(e) {
                                        if (e.response === 1) {
                                            this._QABWidget.restoreFactoryControls();
                                        }
                                    })
                                }
                            );
                        }
                    }));
                }
            }

            // Only show the "Minimize Toolstrip" menu item if the context menu is actually opening for a child of a Toolstrip
            if ((this.get("displayState") !== DisplayStateConstants.STATIC) && (matchesSelector(e.target, '[data-type="Toolstrip"], [data-type="Toolstrip"] *') ||
                matchesSelector(e.target, '[data-type="SectionPopup"], [data-type="SectionPopup"] *'))) {
                // Add a separator between last child and "Minimize Toolstrip" Menu Items
                if (this._contextMenu.hasChildren()) {
                    this._contextMenu.addChild(new MenuSeparator());
                }

                // The minimize toolstrip menu item is always added as the last item of the context menu no matter which
                // widget is the target in the Toolstrip
                var minimizeRestoreToolstripMenuItem = new MenuItem({
                    text: this.get("displayState") !== DisplayStateConstants.EXPANDED ? toolstripL10n.restoreToolstrip : toolstripL10n.minimizeToolstrip,
                    tag: "minimizeRestoreToolstrip"
                });
                this._contextMenu.addChild(minimizeRestoreToolstripMenuItem);
                minimizeRestoreToolstripMenuItem.on("click", lang.hitch(this, this._expandCollapse));
            }

            // To prevent the Context Menu from closing when opened on a popup from the toolstrip, we need to fake 'dijitpopupparent'
            if (this._contextMenu.hasChildren()) {
                var id,
                    ancestorWidget = registry.getEnclosingWidget(e.target);

                if (ancestorWidget && ancestorWidget.id) {
                    id = ancestorWidget.id;
                } else {
                    id = 'node_id_' + Math.random().toString(36).substring(7);
                    e.target.setAttribute("id", id);
                }

                this._contextMenu.domNode.setAttribute("dijitpopupparent", id);
            }
        },

        /**
         * Returns the corresponding widget that should be added to QAB when a user tries adding the widget
         * (parameter of this function) to the QAB.
         * @param {Object} widget
         * @returns {*}
         * @private
         */
        _findCorrespondingWidgetForIsInQABProp: function (widget) {
            var parent = widget.getParent ? widget.getParent() : null;
            while (parent && parent._invokingWidget === undefined) {
                parent = parent.getParent();
            }

            if (parent && parent._invokingWidget) {
                return parent._invokingWidget;
            }

            return null;
        },

        /**
         * Returns true if the widget resides within a GalleryPopup Footer.
         * @param {Object} widget
         * @returns {*|boolean}
         * @private
         */
        _isWidgetWithinGalleryPopupFooter: function (widget) {
            var footerFound = false, parent = widget.getParent ? widget.getParent() : null;
            while (parent && parent.get("type") !== TypeConstants.GALLERY_POPUP) {
                if (parent.get("type") === TypeConstants.FOOTER) {
                    footerFound = true;
                }
                parent = parent.getParent();
            }

            return (footerFound && parent && parent.get("type") === TypeConstants.GALLERY_POPUP);
        },

        _fireQABEvent: function (toolstripItemId, qabItemId, eventType) {
            var qabId, qabWidget, eventData;
            qabId = this.get("QABId");
            qabWidget = registry.byId(qabId);

            eventData = {
                mwEventData: {
                    itemId: toolstripItemId,
                    qabItemId: qabItemId
                }
            };

            if (eventType === "add") {
                qabWidget.controlAdded(eventData);
            } else {
                qabWidget.controlRemoved(eventData);
            }
        },

        _isListItemTypeWidget: function (widget) {
            switch (widget.get("type")) {
                case TypeConstants.LIST_ITEM:
                case TypeConstants.LIST_ITEM_WITH_POPUP:
                case TypeConstants.LIST_ITEM_WITH_CHECKBOX:
                case TypeConstants.LIST_ITEM_WITH_RADIO_BUTTON:
                case TypeConstants.LIST_ITEM_WITH_TEXTFIELD:
                    return true;

                default:
                    return false;
            }
        },

        _setShowHideLabelMenuItem: function (widget) {
            // Do not show 'Show Label'/'Hide Label' context menu item within the QAB overflpow popup
            if (!this._QABWidget._overflowPopup.domNode.contains(widget.domNode)) {
                var showHideLabelMenuItem = new MenuItem({
                    text: widget.get("showText") ? toolstripL10n.hideLabel : toolstripL10n.showLabel,
                    tag: "showHideLabel"
                });
                this._contextMenu.addChild(showHideLabelMenuItem);
                showHideLabelMenuItem.on("click", function () {
                    widget.set("showText", !widget.get("showText"));

                    // g2025678: Resize the toolstrip to make sure that QAB does not overlap with the toolstrip tabs
                    this.resize();
                }.bind(this));
            }
        },

        _addRemoveWidgetMenuItemToContextMenu: function (widget) {
            var removeWidgetMenuItem,
                fireRemove = function (e) {
                    if ((e.type === "keydown") && ([keys.ENTER, keys.SPACE].indexOf(e.keyCode) < 0)) {
                        return;
                    }

                    if (widget._emitRemoveEvent) {
                        widget._emitRemoveEvent();
                    } else { // Unknown Widget
                        widget.emit("remove");
                    }
                },
                menuItemText = widget.get("removeMessage");

            if (!menuItemText) {
                switch (widget.get("type")) {
                    case TypeConstants.TAB:
                        // Using the syntax of "dojo/string.substitute()" for consistency with
                        // other UI components with similar behavior.
                        menuItemText = toolstripL10n.closeWidget.replace(/\$\{0}/g, widget.get("title"));
                        break;
                    case TypeConstants.GALLERY_ITEM:
                    case TypeConstants.TOGGLE_GALLERY_ITEM:
                    case TypeConstants.LIST_ITEM:
                    case TypeConstants.LIST_ITEM_WITH_TEXTFIELD:
                        menuItemText = toolstripL10n.removeWidget.replace(/\$\{0}/g, widget.get("text"));
                        break;
                }
            }

            removeWidgetMenuItem = new MenuItem({
                text: menuItemText,
                tag: "removeWidget"
            });
            
            this._contextMenu.addChild(removeWidgetMenuItem);

            removeWidgetMenuItem.on("mouseup", fireRemove);
            removeWidgetMenuItem.on("keydown", fireRemove);
        },

        _addEditWidgetMenuItemToContextMenu: function (widget) {
            var editWidgetMenuItem,
                fireEdit = function (e) {
                    if ((e.type === "keydown") && ([keys.ENTER, keys.SPACE].indexOf(e.keyCode) < 0)) {
                        return;
                    }

                    if (widget._emitEditEvent) {
                        widget._emitEditEvent();
                    } else { // Unknown Widget
                        widget.emit("edit");
                    }
                },
                menuItemText = widget.get("editOptionMessage");

            if (!menuItemText) {
                switch (widget.get("type")) {
                    case TypeConstants.TAB:
                        // Using the syntax of "dojo/string.substitute()" for consistency with
                        // other UI components with similar behavior.
                        menuItemText = toolstripL10n.editWidget.replace(/\$\{0}/g, widget.get("title"));
                        break;
                    case TypeConstants.GALLERY_ITEM:
                    case TypeConstants.TOGGLE_GALLERY_ITEM:
                    case TypeConstants.LIST_ITEM:
                    case TypeConstants.LIST_ITEM_WITH_TEXTFIELD:
                        menuItemText = toolstripL10n.editWidget.replace(/\$\{0}/g, widget.get("text"));
                        break;
                }
            }

            editWidgetMenuItem = new MenuItem({
                text: menuItemText,
                tag: "editWidget"
            });

            this._contextMenu.addChild(editWidgetMenuItem);
            editWidgetMenuItem.on("mouseup", fireEdit);
            editWidgetMenuItem.on("keydown", fireEdit);
        },

        _isDescendantOfQAB: function (widget) {
            var result = false;

            if (widget === this._QABWidget) {
                result = true;
            } else {
                while (widget && widget.getParent && widget.getParent() !== this) {
                    if (widget.getParent() === this._QABWidget || widget.getParent() === this._QABWidget._overflowPopup) {
                        result = true;
                        break;
                    }
                    widget = widget.getParent();
                }
            }

            return result;
        },

        _getToolstripWidth: function () {
            return domGeometry.getMarginBox(this.domNode).w;
        },
        _getTopRightContainerWidth: function () {
            return domGeometry.getMarginBox(this.topRightContainer).w;
        },

        _getTabsWidth: function (tabs) {
            var currentTabsWidth = 0;
            tabs.forEach(function (tab) {
                currentTabsWidth += domGeometry.getMarginBox(tab).w;
            });
            return currentTabsWidth;
        },

        _getTabMargin: function (tabs) {
            if (tabs.length > 0) {
                return domGeometry.getMarginBox(tabs[0]).w - domGeometry.position(tabs[0]).w;
            }
        },
        _getTabPadding: function (tabs) {
            if (tabs.length > 0) {
                if (!this._tabPadding) {
                    const tabStyles = window.getComputedStyle(tabs[0]);
                    this._tabPadding = parseFloat(tabStyles.paddingLeft) + parseFloat(tabStyles.paddingRight);
                }
                return this._tabPadding;
            }
            return 0;
        },
        _shrinkTabs: function (tabs) {
            var numberOfTabs = tabs.length, that = this, tabMargin = this._getTabMargin(tabs),  tabPadding = this._getTabPadding(tabs);
            tabs.forEach(function (tab, counter) {
                var newWidth = (that._availableWidthForTabs / numberOfTabs) - tabPadding - tabMargin;
                //When actual size is less than its padding, ignore the padding
                tabs[counter].style.setProperty("width", newWidth + "px");
                tab.classList.add("noMinWidth");
            });
        },
        _expandTabs: function (tabs) {
            var currentTabWidth, newTabWidth, that = this, allTabsWidth = 0, numberOfTabs = tabs.length, actualTabWidth, individualTabWidth = [], tabMargin = this._getTabMargin(tabs), tabPadding = this._getTabPadding(tabs);
            tabs.forEach(function (tab, counter) {
                // Computing Actual Width of the tab: it is the maximum width to which any tab can grow
                actualTabWidth = ToolstripUtils.computeWidth(tabs[counter].textContent, null, "font-size: 11px");
                // Text like "HOME" will not be of Tab size: 102 px, so have 102px as its size
                if (actualTabWidth < MiscConstants.TAB_WIDTH) {
                    actualTabWidth = MiscConstants.TAB_WIDTH;
                }
                actualTabWidth = actualTabWidth + tabMargin + tabPadding;

                allTabsWidth = allTabsWidth + actualTabWidth;
                individualTabWidth.push(actualTabWidth);

            });

            if(allTabsWidth < (that._getToolstripWidth() - that._getTopRightContainerWidth() )) {
                tabs.forEach(function (tab, counter) {
                    tab.classList.remove("noMinWidth");
                    if ([].indexOf.call(tab.style, "width") > -1) {
                        tabs[counter].style.width = "";
                    }
                });
            } else {
                tabs.forEach(function (tab, counter) {
                    currentTabWidth = parseFloat(tabs[counter].style.width);
                    newTabWidth = currentTabWidth + (that._availableWidth / numberOfTabs);
                    //When actual size is less than its padding, ignore the padding
                    if (newTabWidth < individualTabWidth[counter]) {
                        tabs[counter].style.width = newTabWidth + "px";
                        tab.classList.add("noMinWidth");
                    } else {
                        tabs[counter].style.width =
                                individualTabWidth[counter] - tabMargin + "px";
                    }
                });
            }
        },
        
        _setPreventResizeAttr: function (value) {
            this._doNotResize = value;
            this._ignoreResize = value;
        },

        // Resize toolstrip only after 0.5 sec
        resizeToolstripWithDelay: function () {
            if (this._resizeTimeout) {
                clearTimeout(this._resizeTimeout);
                this._resizeTimeout = null;
            }
            this._resizeTimeout = setTimeout(function () {
                this.resize();
            }.bind(this), 500);
        },

        setResizeFlag: function (value) {
            this._doNotResize = !value;
        },
        
        resize: function () {
            if (!this._startupComplete) {
                return;
            }

            this.inherited(arguments);

            // Ignore any resize called after the Tab has already been destroyed. This can happen due to the optimization
            // of user interaction done in Toolstrip.js involving the use of setTimeout.
            if (this._destroyed || this._doNotResize) {
                return;
            }

            if (this.tabContainer._started) {
                this.tabContainer.resize();
            }

            //Make sure Toolstrip is placed, before resizing
            //There can be cases where toolstrip is created and later placed
            if (this.domNode.parentElement) {
                var tabs, that = this, collapsePriorityOrder, containerWidths, currentTabsWidth = 0, overlappedWidth, topRightContainerWidth, toolstripWidth;
                tabs = [].slice.call(this.domNode.querySelectorAll(".mwTabButton"));
                currentTabsWidth = this._getTabsWidth(tabs);
                toolstripWidth = this._getToolstripWidth();
                topRightContainerWidth = this._getTopRightContainerWidth();
                containerWidths = {
                    "QABContainer": this._getQABWidth(),
                    "logoutContainer": this._getLogoutButtonWidth(),
                    "searchContainer": this._getSearchBoxWidth(),
                    "tabs": this._getTabsWidth(tabs)
                };
                //Define the priority of Reducing size
                collapsePriorityOrder = ["searchContainer", "QABContainer", "tabs"];
                //overlap occurs
                if (currentTabsWidth + topRightContainerWidth + this._topLeftButtonWidth > toolstripWidth) {
                    overlappedWidth = (currentTabsWidth + topRightContainerWidth + this._topLeftButtonWidth) - toolstripWidth;
                    collapsePriorityOrder.forEach(function (type) {
                        // Recompute widths every loop iteration as widths change
                        topRightContainerWidth = that._getTopRightContainerWidth();
                        toolstripWidth = that._getToolstripWidth();
                        overlappedWidth = (currentTabsWidth + topRightContainerWidth + this._topLeftButtonWidth) - toolstripWidth;
                        if (overlappedWidth > 0) {
                            if (type === "tabs") {
                                that._availableWidthForTabs = toolstripWidth - topRightContainerWidth - this._topLeftButtonWidth;
                                that._shrinkTabs(tabs);
                            } else if (type === 'searchContainer') {
                                that.searchPopupContainer.classList.remove("dijitOffScreen");
                                that.searchContainer.classList.add("dijitOffScreen");
                            } else {
                                if (this._QABWidget && this._QABWidget.collapse) {
                                    this._QABWidget.collapse(overlappedWidth);
                                }
                            }
                        }
                    }.bind(this));
                } else {
                    this._availableWidth = toolstripWidth - (currentTabsWidth + topRightContainerWidth + this._topLeftButtonWidth);
                    collapsePriorityOrder.reverse().forEach(function (type) {
                        // Recompute widths every loop iteration as widths change
                        topRightContainerWidth = that._getTopRightContainerWidth();
                        toolstripWidth = that._getToolstripWidth();
                        currentTabsWidth = that._getTabsWidth(tabs);
                        that._availableWidth = toolstripWidth - (currentTabsWidth + topRightContainerWidth + this._topLeftButtonWidth);
                        if (that._availableWidth > 0) {
                            if (type === "tabs") {
                                that._expandTabs(tabs);
                            } else if (type === 'QABContainer') {
                                if (this._QABWidget && this._QABWidget.expand) {
                                    this._QABWidget.expand(that._availableWidth);
                                }
                            } else {
                                if (containerWidths['searchContainer'] < that._availableWidth) {
                                    query("." + type, that.domNode)[0].classList.remove("dijitOffScreen");
                                    query("." + type, that.domNode)[0].style.display = "inline-block";
                                    that.searchPopupContainer.classList.add("dijitOffScreen");
                                }
                            }
                        }
                    }.bind(this));
                }
            }

            // TODO: Enable Fading once finalized with Matt
//            tabs.forEach(function (tab, counter) {
//                // Computing Actual Width of the tab: it is the maximum width to which any tab can grow
//                var tabWidth = domGeometry.position(tab).w;
//                if (tabWidth < MiscConstants.TAB_WIDTH && (ToolstripUtils.computeWidth(labelNodes[counter].innerHTML) + 20 > tabWidth)) {
//                    domClass.add(tab, "collapsedTab");
//                } else {
//                    domClass.remove(tab, "collapsedTab");
//                }
//
//            });
        },

        destroy: function() {
            if (this._resizeTimeout) {
                clearTimeout(this._resizeTimeout);
                this._resizeTimeout = null;
            }
            this.domNode.removeEventListener("keydown", this._handleKeydown, true);
            document.removeEventListener("click", this._handleClick, true);
            if (this._handleLongPress) {
                this.domNode.removeEventListener("longpress", this._handleLongPress);
            }
            if (this._topLeftButtonListeners) {
                this._topLeftButtonListeners.forEach(function (listener) {
                    listener.remove();
                });
            }
            this.inherited(arguments);
        },

        _getLogoutButtonWidth: function () {
            return domGeometry.getMarginBox(this.logoutContainer).w;
        },

        _getQABWidth: function () {
            return domGeometry.getMarginBox(this.QABContainer).w;
        },

        _getSearchBoxWidth: function () {
            return domGeometry.getMarginBox(this.searchContainer).w;

        },


        startup: function () {
            this.inherited(arguments);
            this._adjustHostHeight();

            // To support EXPANDED_ON_TOP state
            var parentNode = this.domNode.parentNode;
            if (parentNode) {
                // The z-index needs to be high enough to be over content when in expanded on top state but low enough
                // to be under dialog's underlay. For example: The dialog underlay's z-index is 949,
                // the dialog's z-index is 100, the border container splitter's z-index is 10. So, the Toolstrip's
                // z-index has to be higher than splitters but lower than dialogs etc.
                parentNode.style.zIndex = 20;
            }

            this.tabContainer.startup();

            this.collapseButton.startup();

            if (this._QABWidget) {
                this._QABWidget.startup();
            }

            this._startupComplete = true;
            if (this._topLeftButton) {
                this._topLeftButtonWidth = parseFloat(window.getComputedStyle(this._topLeftButton).width);
            } else {
                this._topLeftButtonWidth = 0;
            }
        },

        _expandCollapse: function () {
            var ds = this.displayState;

            if (ds === DisplayStateConstants.COLLAPSED ||
                ds === DisplayStateConstants.EXPANDED_ON_TOP) {
                ds = DisplayStateConstants.EXPANDED;
            } else if (ds === DisplayStateConstants.EXPANDED) {
                ds = DisplayStateConstants.COLLAPSED;
            }

            this.set("displayState", ds);

            // Close any open popups
            popup.close();
        },

        addTab: function (widget, index) {
            this._shouldSetSelectedTab = false;
            this.tabContainer.addChild(widget, index);
            this._shouldSetSelectedTab = true;

            // if this is the first child, then select it. this is dojo's default behavior (StackContainer's addChild)
            // however, if the tabContainer is not started it would skip this logic. setting it manually to fix geck g1865907
            if(!this.tabContainer.selectedChildWidget){
                this.tabContainer.selectChild(widget);
            }
        },

        removeChild: function (widget) {
            widget.tabs.forEach(function (tab) {
                this.removeTab(tab);
            }, this);
            this.inherited(arguments);
            this.resize();
            // Remove the handle for Toolstrip Resize
            Object.keys(this._tabGroupListeners).forEach(function (widgetId) {
                this._tabGroupListeners[widgetId].forEach(function (widgetHandle) {
                    widgetHandle.remove();
                });
            }, this);
        },

        _beforeAddChild: function (widget, index) {
            var newTabGroupIndex = index, numTabGroups = this.getChildren().length;

            if (!index && index !== 0) {
                newTabGroupIndex = numTabGroups;
            }
            return [widget, newTabGroupIndex];
        },

        addChild: function (widget, index) {
            var i = 0, tabCounter = 0, tabGroups, ChildAddedHandle, ChildRemovedHandle, unSelectableAttributeHandle;

            tabGroups = this.getChildren();

            while (i < index) {
                tabCounter += tabGroups[i].tabs.length;
                i += 1;
            }

            widget.tabs.forEach(function (tab, i) {
                this.addTab(tab, tabCounter + i);
            }, this);

            this.inherited(arguments);
            this.set("selectedTab", widget.get("selectedTab"));
            if (this._startupComplete) {
                this.resize();
            }

            //For IE9, allow all tab labels to take focus and then in toolstrip.js, disallow selection
            // Geck 1223279
            this._removeUnSelectableAttribute();
            ChildAddedHandle = widget.on("childadded", lang.hitch(this, this.resize));
            ChildRemovedHandle = widget.on("childremoved", lang.hitch(this, this.resize));
            unSelectableAttributeHandle = widget.on("childadded", lang.hitch(this, this._removeUnSelectableAttribute));
            this._tabGroupListeners[widget.id] = [ChildAddedHandle, ChildRemovedHandle, unSelectableAttributeHandle];

        },

        _removeUnSelectableAttribute: function () {
            //For IE9, allow all tab labels to take focus
            // Geck 1223279
            query(".mwTabButtonTextNode", this.domNode).forEach(function (node) {
                dom.setSelectable(node, true);
            }, this);
        },

        _setSelectedTabAttr: function (value) {
            if (this._shouldSetSelectedTab) {
                // if (value)   // With ViewModel the selected tab is coming out empty. Need to find out more about that
                    var tabWidget = registry.byId(value.toString());
                if (tabWidget && (this.selectedTab !== value || !this.tabContainer.selectedChildWidget || this.tabContainer.selectedChildWidget.get("id") !== value)) {
                    this._set("selectedTab", value);
                    this.selectTab(tabWidget);
                    this.getChildren().forEach(function (child) {
                        if (child.tabs.some(function (tab) {
                            return tab.id === value;
                        })) {
                            child.set("selectedTab", value);
                        }
                    });
                }
            }
        },

        _setDisplayModeAttr: function (value) {
            if (!Object.keys(DisplayModeConstants).some(function (key) {
                return value === DisplayModeConstants[key];
            })) {
                throw new Error('Expected one of the following inputs: MW/toolstrip/constants/DisplayModeConstants[ "' + Object.keys(DisplayModeConstants).toString().replace(',', '" || "') + '" ]');
            }
            this.domNode.classList.remove(this.displayMode + "Toolstrip");
            this._set("displayMode", value);
            this.domNode.classList.add(this.displayMode + "Toolstrip");
            this._adjustHostHeight();
            this.resize();
            
            if (value === DisplayModeConstants.COMPACT) {
                var contextMenuTargetQuerySelector = '.compactToolstrip.toolstrip .mwWidget, .compactToolstrip.toolstrip .mwWidget *',
                    contextMenuCallback = function (contextMenuBuilder, targetNode) {
                        /*
                            contextMenuBuilder
                                addItems(items)
                                addItem(item, sectionTag)
                                placeItemBefore(item, peer)
                                placeItemAfter(item, peer)
                                placeItemIn(item, sectionTag)
                                removeItem(item)
                                getItems(sectionTag)
                                getItemSection(item)
                                hasItem(item)
                                placeSectionBefore(sectionTag, peerTag)
                                placeSectionAfter(sectionTag, peerTag)
                                getSections()
                                hasSection(sectionTag)
                        */
                        var widget = dijit.getEnclosingWidget(targetNode);
                        
                        var header = new MenuHeader({text: widget.get("text")});
                        header.domNode.classList.add("mwMenuTooltip");
                        
                        if (widget.get("description")) {
                            var description = widget.get("description");
                            
                            var descriptionNode = document.createElement("span");
                            descriptionNode.classList.add("mwDescriptionLine");
                            descriptionNode.textContent = description;
                            
                            header.domNode.appendChild(descriptionNode);
                        }
                        
                        contextMenuBuilder.addItem(header);
                    };

                this._handleLongPress = function (e) {
                    // Open the touch menu
                    this.addContextMenuCallback(contextMenuTargetQuerySelector, contextMenuCallback);
                    this._updateContextMenu(e);
                    
                    // Get the location of the target node
                    var targetWidget = registry.getEnclosingWidget(e.target);
                    var rect = targetWidget.domNode.getBoundingClientRect();
                    var x = rect.left;
                    var verticalOffset = 3;
                    var y = rect.bottom + verticalOffset;
                    
                    // TODO: update this logic to allow keyboard and screenreader support in future
                    var focusOnOpen = this._contextMenu._focusOnOpen;
                    this._contextMenu._focusOnOpen = false;
                    this._contextMenu._showMenu({target: e.target, around: targetWidget.domNode, popup: this._contextMenu, doNotClose: false});
                    this._contextMenu.domNode.parentElement.classList.add("mwToolstripPopupContextMenuCalloutBody");
                    
                    // Create the longpress menu callout arrow node
                    var longpressContextMenuCloseListener = function (e) {
                        document.body.removeChild(calloutArrow);
                        this._contextMenu.domNode.removeEventListener("close", longpressContextMenuCloseListener);
                        this._contextMenu.domNode.parentElement.classList.remove("mwToolstripPopupContextMenuCalloutBody");
                    }.bind(this);
                    this._contextMenu.domNode.addEventListener("close", longpressContextMenuCloseListener);
                    var calloutArrow = document.createElement('div');
                    calloutArrow.classList.add('mwToolstripPopupContextMenuCalloutArrow');
                    calloutArrow.style.left = x + "px";
                    calloutArrow.style.top = y + 10 + "px"; // 10px is the height of the arrow
                    document.body.appendChild(calloutArrow);
                    
                    // Get behind the timeout in onOpenMixin's onOpen method
                    setTimeout(function () {
                        this._contextMenu._focusOnOpen = focusOnOpen;
                    }.bind(this), 0);
                    this.removeContextMenuCallback(contextMenuTargetQuerySelector, contextMenuCallback);
                }.bind(this);

                this.domNode.addEventListener("longpress", this._handleLongPress);

                this._addTouchListeners();
            } else {
				if (this._handleLongPress) {
	                this.domNode.removeEventListener("longpress", this._handleLongPress);
	                delete this._handleLongPress;
    	        }
                this._removeTouchListeners();
            }
        },

        _setDisplayStateAttr: function (value) {
            this._set("displayState", value);
            this._setTooltipforExpandCollapseButton();
            this._adjustHostHeight();
            this._handleStaticState();
            this._handleInteractabilityOfIframesOnPage();
        },
        
        _handleStaticState: function () {
            if (this.displayState === DisplayStateConstants.STATIC) {
                this.collapseButtonContainer.style.display = "none";
                document.body.classList.add("staticToolstrip");
            } else {
                this.collapseButtonContainer.style.display = "";
                document.body.classList.remove("staticToolstrip");
            }
            this.resize();
        },
        
        /*
         Expand collapse button shows Tooltip based on the display state of Toolstrip
         collapsed and expanded on top -> restore toolstrip
         expanded - > minimize toolstrip
         */
        _setTooltipforExpandCollapseButton: function () {
            if (this.collapseButton) {
                if (this.displayState === DisplayStateConstants.COLLAPSED || this.displayState === DisplayStateConstants.EXPANDED_ON_TOP) {
                    this.collapseButton.set("description", toolstripL10n.restoreToolstrip);
                } else {
                    this.collapseButton.set("description", toolstripL10n.minimizeToolstrip);
                }
            }
        },

        _adjustHostHeight: function () {
            var toolstripContainer, motwDesktopBorderContainer;

            toolstripContainer = this.domNode.parentElement;

            // TODO: Is there a better way to get to the border container?
            if (toolstripContainer) {
                motwDesktopBorderContainer =
                    registry.getEnclosingWidget(toolstripContainer.parentElement);

                switch (this.displayState) {
                    case DisplayStateConstants.COLLAPSED:
                        this.domNode.classList.remove("toolstripNotCollapsed");
                        this.domNode.classList.remove("toolstripExpandedOnTop");
                        toolstripContainer.style.height = "28px";
                        toolstripContainer.style.overflow = "hidden";

                        if (this.collapseButton) {
                            this.collapseButton.set("icon", "smallExpand");
                        }
                        break;
                    case DisplayStateConstants.EXPANDED:
                    case DisplayStateConstants.STATIC:
                        this.domNode.classList.add("toolstripNotCollapsed");
                        this.domNode.classList.remove("toolstripExpandedOnTop");
                        toolstripContainer.style.height = (this.displayMode === DisplayModeConstants.COMPACT) ? "81px" : "121px";
                        toolstripContainer.style.overflow = "hidden";
                        if (this.collapseButton) {
                            this.collapseButton.set("icon", "smallCollapse");
                        }
                        // See g1155807
                        if (this.tabContainer && this.tabContainer.selectedChildWidget) {
                            this.tabContainer.selectedChildWidget.resize();
                        }
                        break;
                    case DisplayStateConstants.EXPANDED_ON_TOP:
                        this.domNode.classList.add("toolstripNotCollapsed");
                        this.domNode.classList.add("toolstripExpandedOnTop");
                        toolstripContainer.style.height = "28px";
                        toolstripContainer.style.overflow = "visible";
                        if (this.tabContainer && this.tabContainer.selectedChildWidget) {
                            this.tabContainer.selectedChildWidget.resize();
                        }
                        break;
                }
            }

            if (motwDesktopBorderContainer && motwDesktopBorderContainer.resize) {
                motwDesktopBorderContainer.resize();
            }
        },
        
        _iframesOnPage: null,
        _previousFocusNode: null,
        
        _handleInteractabilityOfIframesOnPage: function () {
            switch (this.displayState) {
                case DisplayStateConstants.EXPANDED_ON_TOP:
                    this._iframesOnPage = document.querySelectorAll('iframe');
                    this._iframesOnPage.forEach(function (node) {
                        node.style.pointerEvents = "none";
                    });
                    if (this.domNode && !this._previousFocusNode) {
                        if (document.activeElement.tagName === "IFRAME") {
                            this._handleKeydown = on(this.domNode, "keydown", function (e) {
                                if (e.keyCode === 27) { // [Esc]
                                    if (this._handleKeydown) {
                                        this._handleKeydown.remove();
                                        delete this._handleKeydown;
                                    }
                                    if (this._previousFocusNode) {
                                        this._previousFocusNode.focus();
                                        this._previousFocusNode = null;
                                    }
                                    if (this.domNode) {
                                        this.domNode.dataset.focus = "refuse";
                                        this.domNode.tabIndex = -1;
                                    }
                                }
                            }.bind(this));
                            this._handleCloseEvent = on(document.body, "close", function () {
                                this.set("displayState", DisplayStateConstants.COLLAPSED);
                                if (this._handleCloseEvent) {
                                    this._handleCloseEvent.remove();
                                    delete this._handleCloseEvent;
                                }
                            }.bind(this));
                            this._previousFocusNode = document.activeElement;
                            this.domNode.dataset.focus = "temporary";
                            this.domNode.tabIndex = 0;
                            this.domNode.focus();
                        }
                    }
                    break;
                case DisplayStateConstants.EXPANDED:
                case DisplayStateConstants.COLLAPSED:
                case DisplayStateConstants.STATIC:
                    if (this._iframesOnPage) {
                        this._iframesOnPage.forEach(function (node) {
                            node.style.pointerEvents = "auto"
                        });
                        this._iframesOnPage = null;
                    }
                    if (this._handleKeydown) {
                        this._handleKeydown.remove();
                        delete this._handleKeydown;
                    }
                    if (this._previousFocusNode) {
                        this._previousFocusNode = null;
                    }
                    if (this.domNode) {
                        this.domNode.dataset.focus = "refuse";
                        this.domNode.tabIndex = -1;
                    }
                    if (this._handleCloseEvent) {
                        this._handleCloseEvent.remove();
                        delete this._handleCloseEvent;
                    }
                    break;
            }
        },

        removeTab: function (widget) {
            this._shouldSetSelectedTab = false;

            // Set the selected value to false for currently selected tab before removing it.
            if (widget.get("selected") === true) {
                widget.set("selected", false);
            }
            this.tabContainer.removeChild(widget);
            this._shouldSetSelectedTab = true;
        },

        selectTab: function (widget) {
            this.tabContainer.selectChild(widget);
            this.set('selectedTab', widget.id);
        },

        _setQABIdAttr: function (value) {
            var QABWidget = registry.byId(value);
            if (QABWidget) {
                this._QABWidget = QABWidget;
                domConstruct.place(QABWidget.domNode, this.QABContainer);
                // Avoid premature startup of QAB if the Toolstrip itself hasn't started yet
                if (this._started) {
                    this._QABWidget.startup();
                }
                this.own(QABWidget);
            }
            this._set("QABId", value);
        },

        // An event that is triggered after the toolstrip is placed in the DOM.
        toolstripPlaced: function () {
        }
    });
});
